import {portableContainers,portableContainer} from './portableContainers.mjs';
import {code128} from './storageBarcode.mjs';
import {boxCode,parseStorageScan} from './storageModel.mjs';

export const LABEL_DPI=203;
const MM_PER_DOT=25.4/LABEL_DPI,PT_PER_MM=72/25.4;
export function compactStorageCode(code){
  const container=portableContainer(code);if(container)return container.code.replace('-','');
  const address=parseStorageScan(code);
  if(!address||!['row','box'].includes(address.kind))throw Error('Invalid storage label.');
  return `S${address.safe}R${address.row}${address.box?`B${address.box}`:''}${address.side||''}`;
}
export function portableLabels(){return portableContainers.map(c=>({code:c.code,heading:'RAND / CONTAINER',detail:c.name.toUpperCase()}));}
export function namedStorageLabels(boxes){return boxes.filter(b=>b.location_kind==='container'&&/^STOR-(?:0[1-9]|[1-9][0-9])$/.test(b.code)).sort((a,b)=>a.code.localeCompare(b.code)).map(b=>({code:b.code,heading:'RAND / CONTAINER',detail:portableContainer(b.code).name.toUpperCase()}));}
export function storageLabels(safe,{sides=false,kind='all'}={}){
  if(![1,2,3].includes(safe))throw Error('Choose Safe 1, Safe 2 or Safe 3.');
  const labels=[];
  for(let row=1;row<=(safe===3?7:9);row++){
    if(kind!=='boxes')labels.push({code:`S${safe}-R0${row}`,heading:`SAFE ${safe} / ROW ${row}`,detail:'ROW / DRAWER'});
    if(kind==='rows')continue;
    for(let box=1;box<=4;box++){
      const code=boxCode(safe,row,box),heading=`SAFE ${safe} / ROW ${row}`;
      labels.push({code,heading,detail:`BOX ${box}`});
      if(sides)for(const side of ['L','R'])labels.push({code:`${code}-${side}`,heading,detail:`BOX ${box} / ${side==='L'?'LEFT':'RIGHT'}`});
    }
  }
  return labels;
}
export function thermalLabelLayout(label,widthMm,heightMm){
  if(!Number.isFinite(widthMm)||!Number.isFinite(heightMm)||widthMm<20||widthMm>50||heightMm<25||heightMm>100)throw Error('Use a label 20–50mm wide and 25–100mm high.');
  const payload=compactStorageCode(label.code),barcode=code128(payload);
  // Whole printer dots avoid softened narrow bars; leave a quiet margin.
  const availableDots=Math.floor(Math.min(widthMm-3,48)/MM_PER_DOT);
  const moduleDots=Math.floor(availableDots/barcode.width);
  if(moduleDots<2)throw Error('This label is too narrow for a clear barcode. Use at least 40mm width.');
  const barcodeWidth=barcode.width*moduleDots*MM_PER_DOT;
  const x=Math.round(((widthMm-barcodeWidth)/2)/MM_PER_DOT)*MM_PER_DOT;
  const barHeight=Math.min(22,heightMm-16);
  return {widthMm,heightMm,payload,moduleDots,barcode,barcodeWidth,x,barTop:10,barHeight};
}

const num=value=>Number(value.toFixed(4)).toString();
const escape=value=>String(value).replace(/[\\()]/g,'\\$&');
const textWidth=(value,size)=>[...value].reduce((n,c)=>n+(/[1Iil /]/.test(c)?.278:/[MW]/.test(c)?.833:/[0-9]/.test(c)?.556:.667)*size,0);
export function thermalLabelsPdf(labels,widthMm,heightMm){
  if(!labels.length)throw Error('Choose at least one label.');
  const layouts=labels.map(label=>thermalLabelLayout(label,widthMm,heightMm));
  // Only built-in fonts and vector rectangles. No images, remote calls or libraries.
  const objects=[],add=s=>{objects.push(s);return objects.length;};
  const catalog=add(''),pages=add(''),font=add('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>'),pageIds=[];
  for(let i=0;i<labels.length;i++){
    const l=layouts[i],label=labels[i],w=widthMm*PT_PER_MM,h=heightMm*PT_PER_MM;
    const center=(value,size,y)=>`BT /F1 ${size} Tf 1 0 0 1 ${num((w-textWidth(value,size))/2)} ${num(h-y*PT_PER_MM)} Tm (${escape(value)}) Tj ET`;
    const commands=['0 g',center(label.heading,8,3.8),center(label.detail,10,8),...l.barcode.bars.map(b=>`${num((l.x+b.x*l.moduleDots*MM_PER_DOT)*PT_PER_MM)} ${num(h-(l.barTop+l.barHeight)*PT_PER_MM)} ${num(b.width*l.moduleDots*MM_PER_DOT*PT_PER_MM)} ${num(l.barHeight*PT_PER_MM)} re f`),center(label.code,7,heightMm-2.3)];
    const stream=commands.join('\n');
    const content=add(`<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`);
    pageIds.push(add(`<< /Type /Page /Parent ${pages} 0 R /MediaBox [0 0 ${num(w)} ${num(h)}] /Rotate 0 /Resources << /Font << /F1 ${font} 0 R >> >> /Contents ${content} 0 R >>`));
  }
  objects[catalog-1]=`<< /Type /Catalog /Pages ${pages} 0 R /ViewerPreferences << /PrintScaling /None >> >>`;
  objects[pages-1]=`<< /Type /Pages /Count ${pageIds.length} /Kids [${pageIds.map(n=>`${n} 0 R`).join(' ')}] >>`;
  let output='%PDF-1.4\n',offsets=[0];
  for(let i=0;i<objects.length;i++){offsets.push(output.length);output+=`${i+1} 0 obj\n${objects[i]}\nendobj\n`;}
  const xref=output.length;
  output+=`xref\n0 ${objects.length+1}\n0000000000 65535 f \n${offsets.slice(1).map(o=>`${String(o).padStart(10,'0')} 00000 n \n`).join('')}trailer\n<< /Size ${objects.length+1} /Root ${catalog} 0 R >>\nstartxref\n${xref}\n%%EOF\n`;
  return new TextEncoder().encode(output);
}
